// src/components/gallery/CharacterFanartGrid.tsx
/**
 * キャラクターファンアートのグリッド表示コンポーネント
 * ファンアート画像をグリッドで表示し、オーナーには承認/却下UIを提供
 * キャッシュ対応: orifoliCacheでフィードをキャッシュ
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoIcon, PlusIcon, CheckIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import { getCharacterFanart, moderateFanart, FanartImage, GalleryImage } from '../../services/characterService';
import XorPixViewer from '../xorpix/XorPixViewer';
import LikeButton from '../ui/LikeButton';
import { orifoliCache, CachedFeedEntry } from '../../services/orifoliCache';

interface CharacterFanartGridProps {
  characterId: string;
  isOwner: boolean;
  onImageClick?: (image: GalleryImage) => void;
  onUploadClick?: () => void;
}

// ファンアートカードコンポーネント
const FanartCard: React.FC<{
  image: FanartImage;
  isOwner: boolean;
  moderationEnabled: boolean;
  onClick: () => void;
  onModerate: (modelId: string, action: 'approve' | 'reject') => void;
}> = ({ image, isOwner, moderationEnabled, onClick, onModerate }) => {
  const { t } = useTranslation('character');
  const [isModerateLoading, setIsModerateLoading] = useState(false);

  // シェアURL生成
  const getShareUrl = (): string => {
    const isProduction = import.meta.env.VITE_API_BASE_URL === 'https://api.figryne.com';
    const bucketName = isProduction ? 'figryne-public-assets-prod' : 'figryne-public-assets';
    return isProduction
      ? `https://figryne.com/share/pix/${image.modelId}`
      : `https://storage.googleapis.com/${bucketName}/share/pix/${image.modelId}.html`;
  };

  // X シェア
  const handleShareX = (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = getShareUrl();
    const title = image.title || 'Fanart';
    const text = `${title} | ORIFOLI`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Bluesky シェア
  const handleShareBluesky = (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = getShareUrl();
    const title = image.title || 'Fanart';
    const text = `${title} | ORIFOLI`;
    window.open(
      `https://bsky.app/intent/compose?text=${encodeURIComponent(text + ' ' + shareUrl)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // 承認/却下
  const handleModerate = async (e: React.MouseEvent, action: 'approve' | 'reject') => {
    e.stopPropagation();
    setIsModerateLoading(true);
    try {
      await onModerate(image.modelId, action);
    } finally {
      setIsModerateLoading(false);
    }
  };

  const isPending = image.fanartStatus === 'pending';
  const isRejected = image.fanartStatus === 'rejected';

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden relative">
      {/* ステータスバッジ（オーナーまたは自分の投稿の場合に表示） */}
      {(isOwner || image.isMine) && isPending && (
        <div className="absolute top-2 left-2 z-10 px-2 py-1 bg-yellow-500 text-white text-xs font-medium rounded-full">
          {t('fanart.pending')}
        </div>
      )}
      {(isOwner || image.isMine) && isRejected && (
        <div className="absolute top-2 left-2 z-10 px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full">
          {t('fanart.rejected')}
        </div>
      )}

      {/* 画像エリア */}
      <div
        onClick={isRejected ? undefined : onClick}
        className={`aspect-square overflow-hidden group relative ${isRejected ? 'opacity-50' : 'cursor-pointer'}`}
      >
        <XorPixViewer
          noise1Url={image.noise1Url}
          noise2Url={image.noise2Url}
          rid={image.rid}
          width={image.width}
          height={image.height}
          characterId={image.modelId}
          fallbackNoise1Url={image.fallbackNoise1Url}
          fallbackNoise2Url={image.fallbackNoise2Url}
          fitMode="cover"
          className="w-full h-full group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />
      </div>

      {/* オーナー用モデレーションボタン */}
      {isOwner && moderationEnabled && isPending && (
        <div className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-200 dark:bg-gray-700">
          <button
            onClick={(e) => handleModerate(e, 'approve')}
            disabled={isModerateLoading}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-[#a2d700] hover:bg-[#7ab500] text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <CheckIcon className="w-4 h-4" />
            {t('fanart.approve')}
          </button>
          <button
            onClick={(e) => handleModerate(e, 'reject')}
            disabled={isModerateLoading}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-gray-400 hover:bg-gray-500 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="w-4 h-4" />
            {t('fanart.reject')}
          </button>
        </div>
      )}

      {/* 通常のアクションバー（承認済みまたはモデレートなしの場合） */}
      {(!isPending || !isOwner || !moderationEnabled) && !isRejected && (
        <div className="flex items-center justify-between px-3 py-2">
          <LikeButton
            modelId={image.modelId}
            initialCount={image.likeCount || 0}
            className="flex items-center gap-1"
            iconClassName="w-4 h-4"
            textClassName="text-xs text-gray-500 dark:text-gray-400"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleShareX}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Share on X"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </button>
            <button
              onClick={handleShareBluesky}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-[#0085ff] transition-colors"
              aria-label="Share on Bluesky"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* 却下済みの場合はシンプルな表示 */}
      {isRejected && (
        <div className="px-3 py-2 text-center">
          <span className="text-xs text-gray-400 dark:text-gray-500">{t('fanart.rejectedNote')}</span>
        </div>
      )}
    </div>
  );
};

const CharacterFanartGrid: React.FC<CharacterFanartGridProps> = ({
  characterId,
  isOwner,
  onImageClick,
  onUploadClick,
}) => {
  const { t } = useTranslation('character');
  const [images, setImages] = useState<FanartImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [moderationEnabled, setModerationEnabled] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const imageMetadataRef = useRef<Map<string, FanartImage>>(new Map());

  // キャッシュからフィードを復元
  const restoreFromCache = useCallback((): boolean => {
    const cachedData = orifoliCache.getFeedEntries('fanart', characterId);
    if (!cachedData || cachedData.entries.length === 0) {
      return false;
    }

    // キャッシュからメタデータを復元
    const cachedImages: FanartImage[] = [];
    for (const entry of cachedData.entries) {
      const metadata = orifoliCache.getImageMetadata(entry.modelId) as FanartImage | null;
      if (metadata) {
        cachedImages.push(metadata);
        imageMetadataRef.current.set(entry.modelId, metadata);
      }
    }

    if (cachedImages.length > 0) {
      setImages(cachedImages);
      setModerationEnabled(cachedData.moderationEnabled || false);
      setHasMore(cachedImages.length >= 20);
      console.log(`[CharacterFanartGrid] Restored ${cachedImages.length} items from cache`);
      return true;
    }
    return false;
  }, [characterId]);

  // キャッシュに保存
  const saveToCache = useCallback((items: FanartImage[], modEnabled: boolean) => {
    if (items.length === 0) return;

    // フィードエントリを保存
    const entries: CachedFeedEntry[] = items.map(item => ({
      modelId: item.modelId,
      created: item.created,
    }));

    const firstTimestamp = items[0]?.created || null;
    const lastTimestamp = items[items.length - 1]?.created || null;

    orifoliCache.saveFeedEntries('fanart', characterId, entries, firstTimestamp, lastTimestamp, modEnabled);

    // メタデータを保存
    items.forEach(item => {
      orifoliCache.saveImageMetadata(item.modelId, item as unknown as Record<string, unknown>);
      imageMetadataRef.current.set(item.modelId, item);
    });
  }, [characterId]);

  // 初回読み込み
  const fetchFanart = useCallback(async (forceRefresh = false) => {
    if (!characterId) return;

    // キャッシュから復元を試みる（強制リフレッシュでない場合）
    if (!forceRefresh && restoreFromCache()) {
      setIsLoading(false);
      // バックグラウンドで新しいアイテムをチェック
      const cachedData = orifoliCache.getFeedEntries('fanart', characterId);
      if (cachedData?.firstTimestamp) {
        try {
          const result = await getCharacterFanart(characterId, 20);
          if (result.items.length > 0 && result.items[0].created !== cachedData.firstTimestamp) {
            // 新しいアイテムがある場合はマージ
            const existingIds = new Set(images.map(img => img.modelId));
            const uniqueNewItems = result.items.filter(item => !existingIds.has(item.modelId));
            if (uniqueNewItems.length > 0) {
              const mergedItems = [...uniqueNewItems, ...images];
              setImages(mergedItems);
              setModerationEnabled(result.moderationEnabled);
              saveToCache(mergedItems, result.moderationEnabled);
            }
          }
        } catch {
          // バックグラウンド更新のエラーは無視
        }
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getCharacterFanart(characterId, 20);
      setImages(result.items);
      setModerationEnabled(result.moderationEnabled);
      setHasMore(result.items.length === 20);
      saveToCache(result.items, result.moderationEnabled);
    } catch (err) {
      console.error('Error fetching fanart:', err);
      setError(t('fanart.loadError'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [characterId, t, restoreFromCache, saveToCache, images]);

  // 追加読み込み
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || images.length === 0) return;

    setIsLoadingMore(true);
    try {
      const lastImage = images[images.length - 1];
      const result = await getCharacterFanart(characterId, 20, lastImage.created);
      const newImages = [...images, ...result.items];
      setImages(newImages);
      setHasMore(result.items.length === 20);
      saveToCache(newImages, moderationEnabled);
    } catch (err) {
      console.error('Error loading more fanart:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [characterId, images, isLoadingMore, hasMore, moderationEnabled, saveToCache]);

  // リフレッシュ
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    orifoliCache.clearFeedCache('fanart', characterId);
    fetchFanart(true);
  }, [characterId, fetchFanart]);

  // モデレーション処理
  const handleModerate = useCallback(async (modelId: string, action: 'approve' | 'reject') => {
    try {
      await moderateFanart(modelId, action);
      // ローカルで状態を更新
      setImages(prev => {
        const updated = prev.map(img =>
          img.modelId === modelId
            ? { ...img, fanartStatus: action === 'approve' ? 'approved' as const : 'rejected' as const }
            : img
        );
        // キャッシュも更新
        saveToCache(updated, moderationEnabled);
        return updated;
      });
    } catch (err) {
      console.error('Error moderating fanart:', err);
    }
  }, [moderationEnabled, saveToCache]);

  useEffect(() => {
    fetchFanart();
  }, [characterId]); // fetchFanartを依存配列から除外して無限ループを防ぐ

  // ローディング状態
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 text-gray-400 dark:text-gray-500 animate-spin" />
      </div>
    );
  }

  // エラー状態
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={() => fetchFanart(true)}
          className="text-purple-600 dark:text-purple-400 hover:underline text-sm"
        >
          {t('actions.retry')}
        </button>
      </div>
    );
  }

  // 空状態
  if (images.length === 0) {
    return (
      <div className="text-center py-12">
        <PhotoIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">{t('fanart.empty')}</p>
        {/* オーナーはファンアートを投稿しない */}
        {onUploadClick && !isOwner && (
          <button
            onClick={onUploadClick}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2 bg-purple-500 text-white rounded-full text-sm font-medium hover:bg-purple-600 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            {t('fanart.post')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-4">
        {/* モデレーション状態表示（オーナーのみ） */}
        {isOwner ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {moderationEnabled
              ? t('fanart.moderationEnabled')
              : t('fanart.moderationDisabled')}
          </div>
        ) : (
          <div />
        )}
        {/* リフレッシュボタン */}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          title={t('actions.refresh')}
        >
          <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* グリッド */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {/* 投稿ボタン - オーナーはファンアートを投稿しない */}
        {onUploadClick && !isOwner && (
          <div className="bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden">
            <button
              onClick={onUploadClick}
              className="aspect-square w-full border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 transition-colors flex flex-col items-center justify-center gap-2 group"
            >
              <PlusIcon className="w-8 h-8 text-gray-400 dark:text-gray-500 group-hover:text-purple-500 transition-colors" />
              <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-purple-500 transition-colors">
                {t('fanart.post')}
              </span>
            </button>
            <div className="h-[40px]" />
          </div>
        )}

        {/* ファンアート一覧 */}
        {images.map((image) => (
          <FanartCard
            key={image.modelId}
            image={image}
            isOwner={isOwner}
            moderationEnabled={moderationEnabled}
            onClick={() => onImageClick?.(image)}
            onModerate={handleModerate}
          />
        ))}
      </div>

      {/* もっと読み込む */}
      {hasMore && (
        <div className="mt-6 text-center">
          <button
            onClick={loadMore}
            disabled={isLoadingMore}
            className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            {isLoadingMore ? (
              <Loader2 className="w-4 h-4 animate-spin inline-block" />
            ) : (
              t('actions.loadMore')
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CharacterFanartGrid;
